import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { ForumApplication, ApiForumApplication } from '../../models/ForumApplication';
import { ForumPost, ApiForumPost } from '../../models/ForumPost';
import { BODY_FIGURE_TEXTS, AGE_RANGE_TEXTS, HEIGHT_RANGE_TEXTS } from '../../consts';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import IconMan from '../../images/common/icon_man11.png';
import IconMan2x from '../../images/common/icon_man11@2x.png';
import './DoneeForumApplications.scss';

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  OPEN:  { label: '掲載中', cls: 'open' },
  DRAFT: { label: '下書き', cls: 'draft' },
};

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${ yyyy }/${ mm }/${ dd } ${ hh }:${ min }`;
}

function calcAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

function IconLicense() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true">
      <path d="M507.342,115.223c-4.474-10.571-11.884-19.57-21.424-26.028c-9.789-6.617-21.214-10.112-33.104-10.112H59.186c-7.994,0-15.744,1.572-23.039,4.657c-10.582,4.478-19.581,11.884-26.032,21.428C3.495,114.972-0.003,126.418,0,138.273v235.454c0,7.987,1.564,15.738,4.658,23.043c4.474,10.586,11.884,19.584,21.425,26.022c9.792,6.623,21.238,10.126,33.104,10.126h393.627c7.976,0,15.726-1.572,23.039-4.658c10.578-4.471,19.581-11.891,26.028-21.436c6.624-9.788,10.122-21.234,10.119-33.097V138.273C512,130.286,510.436,122.535,507.342,115.223z M483.632,373.727c0,4.155-0.814,8.188-2.418,11.985c-2.332,5.518-6.196,10.211-11.18,13.57c-5.088,3.43-11.044,5.254-17.219,5.261H59.186c-4.158,0-8.191-0.811-11.995-2.418c-5.508-2.325-10.198-6.193-13.567-11.181c-3.434-5.088-5.253-11.045-5.256-17.216V138.273c0-4.162,0.814-8.202,2.418-11.999c2.325-5.504,6.19-10.197,11.18-13.57c5.092-3.43,11.044-5.246,17.219-5.246h393.627c4.162,0,8.199,0.811,11.991,2.404c5.508,2.332,10.201,6.2,13.57,11.181c3.434,5.087,5.253,11.051,5.256,17.23V373.727z" />
      <path d="M129.08,261.217c24.834,0,44.963-20.13,44.963-44.96c0-24.837-20.129-44.966-44.963-44.966c-24.83,0-44.96,20.129-44.96,44.966C84.121,241.088,104.25,261.217,129.08,261.217z" />
      <path d="M167.154,268.107c-0.976-0.976-5.411-1.22-6.613-0.488c-9.167,5.655-19.925,8.956-31.46,8.956c-11.539,0-22.293-3.301-31.458-8.956c-1.209-0.732-5.637-0.488-6.616,0.488c-7.546,7.549-17.496,24.399-19.502,36.427c-4.938,29.609,26.692,40.302,57.576,40.302c30.886,0,62.512-10.693,57.579-40.302C184.654,292.506,174.707,275.656,167.154,268.107z" />
      <rect x="233.244" y="180.584" width="202.124" height="22.49" />
      <rect x="233.244" y="244.087" width="202.124" height="22.497" />
      <rect x="233.244" y="307.598" width="125.392" height="22.49" />
    </svg>
  );
}

function IconFlask() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true">
      <path d="M479.949,409.402L353.612,207.617c-10.665-17.049-16.32-36.732-16.32-56.837L337.47,0.164L175.507,0l-0.164,150.498c-0.024,20.128-5.726,39.858-16.438,56.906L32.122,408.932c-6.83,10.865-10.276,23.268-10.276,35.673c0,11.122,2.763,22.293,8.324,32.381c11.805,21.374,34.251,34.638,58.648,34.662L423.054,512h0.022c0.906,0,1.364-0.047,1.376-0.047l-0.012-0.048c23.856-0.47,45.702-13.521,57.318-34.426v-0.024c5.61-10.111,8.396-21.316,8.396-32.486C490.154,432.612,486.733,420.243,479.949,409.402z M447.556,458.514c-4.915,8.877-14.273,14.403-24.42,14.379c-0.106,0-0.129,0.024-0.2,0.024l-334.082-0.365c-10.159-0.012-19.542-5.549-24.432-14.45c-2.341-4.233-3.481-8.842-3.481-13.498c0-5.186,1.423-10.323,4.28-14.862l126.76-201.515c14.65-23.28,22.41-50.193,22.456-77.706l0.118-111.392l83.774,0.094l-0.129,111.416v0.047v0.094c0,27.442,7.725,54.308,22.28,77.578l126.325,201.784c2.834,4.515,4.256,9.666,4.268,14.827C451.06,449.637,449.907,454.269,447.556,458.514z" />
      <path d="M323.642,291.296c-1.434-2.316-3.973-3.716-6.689-3.716l-121.551-0.129c-2.728,0-5.268,1.387-6.703,3.704L101.504,429.72c-1.528,2.445-1.622,5.526-0.223,8.054c1.387,2.528,4.044,4.08,6.925,4.08l295.599,0.318c2.881,0.011,5.526-1.541,6.937-4.068c1.387-2.528,1.305-5.597-0.234-8.03L323.642,291.296z" />
    </svg>
  );
}

const DoneeForumApplications = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ post, setPost ] = useState<ForumPost>();
  const [ applications, setApplications ] = useState<ForumApplication[]>();
  const { get: getPost, loading: loadingPost } = useApi();
  const { get: getApplications, loading: loadingApplications } = useApi();

  useEffect(() => {
    getPost({
      endpoint: `api/v1/forum_posts/${ id }`,
      onSuccess: (res) => { setPost(new ForumPost(res.data as ApiForumPost)); },
      onError: () => { toast.error('投稿の取得に失敗しました'); },
    });
  }, [ getPost, id ]);

  useEffect(() => {
    getApplications({
      endpoint: `api/v1/forum_posts/${ id }/forum_applications`,
      onSuccess: (res) => {
        setApplications((res.data as ApiForumApplication[]).map(a => new ForumApplication(a)));
      },
      onError: () => { toast.error('応募一覧の取得に失敗しました'); },
    });
  }, [ getApplications, id ]);

  const sorted = applications
    ? [ ...applications ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  if (loadingPost || loadingApplications || !post || !applications) {
    return <Spinner centered={ true } color="pink" />;
  }

  const isExpired = post.isOpen && !!post.expiresAt && new Date(post.expiresAt) < new Date();
  const pill = isExpired
    ? { label: '掲載終了', cls: 'closed' }
    : STATUS_PILL[post.status] ?? { label: post.status, cls: 'draft' };
  const isUrgent = post.isOpen && !isExpired && post.daysLeft > 0 && post.daysLeft <= 20;
  const showCountdown = post.isOpen && !isExpired && post.daysLeft > 0;

  return (
    <div className="al-page">
      <nav className="al-breadcrumb">
        <Link to="/mypage/forum">募集の管理</Link>
        <span className="al-bc-sep">›</span>
        <span className="al-bc-current">応募者一覧</span>
      </nav>

      <section className="al-summary">
        <div className="al-summary-head">
          <span className={ `al-status-pill ${ pill.cls }` }>{ pill.label }</span>
        </div>

        <div className="al-summary-meta">
          { post.postedAt && (
            <span>投稿 <strong>{ post.postedAtText }</strong></span>
          ) }
          { post.postedAt && post.expiresAt && <span className="al-dot" /> }
          { post.expiresAt && (
            <span>
              締切 <strong>{ post.expiresAtText }</strong>
              { showCountdown && (
                <em className={ `al-countdown${ isUrgent ? ' urgent' : '' }` }>
                  あと{ post.daysLeft }日
                </em>
              ) }
            </span>
          ) }
        </div>

        { post.description && (
          <p className="al-summary-body">{ post.description }</p>
        ) }

        <div className="al-summary-chips">
          { post.area && (
            <span className="al-chip al-chip-area">{ post.areaText }</span>
          ) }
          { post.bloodTypeChips.map(v => (
            <span key={ v } className="al-chip">{ v }</span>
          )) }
          { post.ageRange?.map(k => (
            <span key={ k } className="al-chip">{ AGE_RANGE_TEXTS[k] || k }</span>
          )) }
          { post.bodyFigure?.map(k => (
            <span key={ k } className="al-chip">{ BODY_FIGURE_TEXTS[k] || k }</span>
          )) }
          { post.heightRange?.map(k => (
            <span key={ k } className="al-chip">{ HEIGHT_RANGE_TEXTS[k] || k }</span>
          )) }
        </div>
      </section>

      <div className="al-list-head">
        <h2>応募ドナー <strong>{ applications.length }</strong>人</h2>
      </div>

      { sorted.length === 0 ? (
        <p className="al-empty">まだ応募はありません。</p>
      ) : (
        <ul className="al-applicant-list">
          { sorted.map(app => {
            const age = calcAge(app.donorBirthDate);
            return (
              <li key={ app.id } className="al-applicant">
                <div className="al-ap-side">
                  <div className="al-ap-avatar">
                    { app.donorPhotoUrl
                      ? <img src={ app.donorPhotoUrl } alt="" />
                      : <img src={ IconMan } srcSet={ `${ IconMan } 1x, ${ IconMan2x } 2x` } alt="" />
                    }
                  </div>
                </div>

                <div className="al-ap-body">
                  <div className="al-ap-head">
                    <strong className="al-ap-name">{ app.donorInitialName }様</strong>

                    <div className="al-ap-verif">
                      <span
                        className={ `al-verif-circle ${ app.donorKyc ? '' : 'off' }` }
                        title="本人確認"
                      >
                        <IconLicense />
                      </span>
                      <span
                        className={ `al-verif-circle ${ app.donorStd ? '' : 'off' }` }
                        title="性病検査"
                      >
                        <IconFlask />
                      </span>
                      <span
                        className={ `al-verif-circle ${ app.donorQuality ? '' : 'off' }` }
                        title="精液品質"
                      >
                        <span className="al-grade">{ app.donorQuality || '—' }</span>
                      </span>
                    </div>

                    <span className="al-ap-applied">{ formatDateTime(app.createdAt) }に応募</span>
                  </div>

                  <div className="al-ap-profile">
                    { app.donorBloodType && (
                      <span className="al-pf"><i>血液型</i>{ app.donorBloodType }型</span>
                    ) }
                    { age !== null && (
                      <span className="al-pf"><i>年齢</i>{ age }歳</span>
                    ) }
                    { app.donorBodyFigure && (
                      <span className="al-pf"><i>体型</i>{ BODY_FIGURE_TEXTS[app.donorBodyFigure] || app.donorBodyFigure }</span>
                    ) }
                    { app.donorHeight && (
                      <span className="al-pf"><i>身長</i>{ app.donorHeight }cm</span>
                    ) }
                  </div>

                  { app.comment && (
                    <p className="al-ap-comment">{ app.comment }</p>
                  ) }

                  <div className="al-ap-actions">
                    <span className="al-ap-actions-note">メッセージはプロフィールから送信できます</span>
                    <button
                      className="al-btn-pink"
                      onClick={ () => navigate(`/donor/${ app.donorUserNo }`) }
                    >
                      プロフィールを見る
                    </button>
                  </div>
                </div>
              </li>
            );
          }) }
        </ul>
      ) }
    </div>
  );
};

export default DoneeForumApplications;
